"use client";

import { useMemo } from "react";
import { useTranslations } from "@zapier/i18n";
import {
  Alert,
  AlertDescription,
  Radio,
  RadioGroup,
  Spinner,
  Text,
} from "@zapier/design-system";
import type { HydratedAsset } from "@zapier/client";
import { AssetTypes } from "@zapier/utils";

import { AssetsSelectedCard } from "../AssetsSelectedCard";
import styles from "./ReviewAndConfirmContent.module.css";

/**
 * Above this many assets we surface a heads-up that the copy can take several
 * minutes. The copy runs as a single in-flight request (not a background job),
 * so the tab must stay open until it resolves.
 */
const LARGE_COPY_THRESHOLD = 500;

interface Props {
  targetAccountName: string;
  destinationFolderName?: string;
  isTopLevelDestination?: boolean;
  assets: HydratedAsset[];
  /**
   * True while the BFF subtree-asset query is in flight (folder-tree copies
   * only). When true we show a spinner in place of the assets card so the
   * count doesn't briefly read "0" while data loads.
   */
  isLoadingAssets?: boolean;
  /**
   * True while the copy request is in flight. The large-copy "keep this tab
   * open" notice only shows while the copy is actually pending — once it
   * resolves or errors, the copy is no longer running so the notice is hidden.
   */
  isCopying?: boolean;
  /** When set, an error Alert is rendered above the warning. */
  errorMessage?: string;
  /**
   * Whether to show the "Copy table records?" choice. Only true when the copy
   * includes at least one table. When false, the section is not rendered and
   * the copy behaves exactly as before.
   */
  showTableRecordsChoice?: boolean;
  /**
   * The current Yes/No selection: `true` = copy records, `false` = structure
   * only, `undefined` = no choice made yet (Copy button stays disabled).
   */
  tableRecordsChoice?: boolean;
  /** Called when the user picks Yes (`true`) or No (`false`). */
  onTableRecordsChoiceChange?: (includeRecords: boolean) => void;
}

/** RadioGroup value sentinel for "no choice made yet" (matches no Radio). */
const NO_CHOICE = "";

/**
 * Body-only content for the "Review & Confirm" step.
 *
 * Follows the "Import Assets Modal" structure but with copy
 * adjusted for the source-account perspective (we're copying out, not
 * importing in): "Copy assets to {accountName}" header, "What you're copying
 * to {accountName}:" label, and "Changes to expect when copying to a new
 * account:" warning. The header + Cancel/Copy assets footer live on the
 * parent.
 */
export function ReviewAndConfirmContent({
  targetAccountName,
  destinationFolderName,
  isTopLevelDestination,
  assets,
  isLoadingAssets,
  isCopying,
  errorMessage,
  showTableRecordsChoice,
  tableRecordsChoice,
  onTableRecordsChoiceChange,
}: Props) {
  const t = useTranslations("copy-to-another-account-modal");
  const destinationDescriptor = isTopLevelDestination
    ? "the top level folder"
    : destinationFolderName
      ? `the "${destinationFolderName}" folder`
      : targetAccountName;
  const destinationLabel = t("review-step.what-youre-copying", {
    accountName: destinationDescriptor,
  });

  const hasZap = useMemo(
    () => assets.some((asset) => asset.type === AssetTypes.Zap),
    [assets],
  );
  const hasTable = useMemo(
    () => assets.some((asset) => asset.type === AssetTypes.Table),
    [assets],
  );

  return (
    <div className={styles.body}>
      <Text type="Body2" tag="p" className={styles.label}>
        {destinationLabel}
      </Text>

      {isLoadingAssets ? (
        <div className={styles.assetsLoading}>
          <Spinner size="medium" aria-label={t("review-step.loading-assets")} />
        </div>
      ) : (
        <AssetsSelectedCard count={assets.length} assets={assets} />
      )}

      {showTableRecordsChoice && (
        <RadioGroup
          label={t("review-step.table-records.label")}
          isRequired
          value={
            tableRecordsChoice === undefined
              ? NO_CHOICE
              : tableRecordsChoice
                ? "yes"
                : "no"
          }
          onChange={(value) => onTableRecordsChoiceChange?.(value === "yes")}
        >
          <Text
            type="SmallPrint1Multiline"
            color="TextWeaker"
            tag="p"
            className={styles.tableRecordsDescription}
          >
            {t("review-step.table-records.description")}
          </Text>
          <Radio value="yes">{t("review-step.table-records.yes")}</Radio>
          <Radio value="no">{t("review-step.table-records.no")}</Radio>
        </RadioGroup>
      )}

      {assets.length > 0 && (
        <Alert status="warning" title={t("review-step.warning-title")}>
          <ul className={styles.warningList}>
            <li>{t("review-step.warning-bullets.missing-deps")}</li>
            <li>{t("review-step.warning-bullets.sharing-reset")}</li>
          </ul>

          {hasZap && (
            <>
              <Text type="Body4" tag="p" className={styles.warningGroupHeading}>
                {/* eslint-disable react/jsx-no-literals -- "Zaps" is a brand/product name, intentionally not translated */}
                Zaps
                {/* eslint-enable react/jsx-no-literals */}
              </Text>
              <ul
                className={styles.warningList}
                data-testid="warning-group-zaps"
              >
                <li>{t("review-step.warning-bullets.published-drafts")}</li>
                <li>{t("review-step.warning-bullets.webhook-url")}</li>
                <li>{t("review-step.warning-bullets.run-history")}</li>
                <li>{t("review-step.warning-bullets.global-vars")}</li>
              </ul>
            </>
          )}

          {hasTable && (
            <>
              <Text type="Body4" tag="p" className={styles.warningGroupHeading}>
                {t("review-step.warning-group-tables")}
              </Text>
              <ul
                className={styles.warningList}
                data-testid="warning-group-tables"
              >
                <li>{t("review-step.warning-bullets.table-views")}</li>
                <li>{t("review-step.warning-bullets.table-ai-fields")}</li>
              </ul>
            </>
          )}
        </Alert>
      )}

      {isCopying && assets.length > LARGE_COPY_THRESHOLD ? (
        <Alert status="info" title={t("review-step.large-copy-title")}>
          <AlertDescription>
            {t("review-step.large-copy-description")}
          </AlertDescription>
        </Alert>
      ) : undefined}

      {errorMessage ? (
        <Alert status="error" title="Error copying">
          <AlertDescription>{errorMessage}</AlertDescription>
        </Alert>
      ) : undefined}
    </div>
  );
}
